import IDBStorage from "../../lib/files/idb";
import DATABASE_STATE from "../../config/databases/state";
import { logger } from "../../lib/logger";
import { getFullNameByPeerId } from "../utils/index";

// 日志类型
export enum AiLogType {
    RECEIVED_MESSAGE = "RECEIVED_MESSAGE",      // 收到用户消息
    AI_SESSION_START = "AI_SESSION_START",      // AI会话开始
    AI_MESSAGE_SENT = "AI_MESSAGE_SENT",        // AI消息发送
    AI_QUERY_RESPONSE = "AI_QUERY_RESPONSE",    // AI查询响应
    QUERY_CHAT = "QUERY_CHAT",                  // query_chat接口响应
    AI_SESSION_END = "AI_SESSION_END",          // AI会话结束
    ERROR = "ERROR",                            // 错误日志
    PERFORMANCE = "PERFORMANCE"                 // 性能监控
}

// 日志级别
export enum AiLogLevel {
    DEBUG = "DEBUG",
    INFO = "INFO",
    WARN = "WARN",
    ERROR = "ERROR"
}

// 日志项接口
export interface AiLogItem {
    timestamp: number;          // 时间戳
    formattedTime: string;      // 格式化时间
    type: AiLogType;            // 日志类型
    level: AiLogLevel;          // 日志级别
    message: string;            // 日志消息
    sessionId?: string;         // 会话ID（可选）
    data?: any;                 // 附加数据（可选）
}

class AiLogger {
    private storage: IDBStorage<typeof DATABASE_STATE, 'aiLogs'>;
    private initialized: boolean = false;
    private log: any; // 使用any类型避免类型错误
    private sessionId: string;
    private sessionStartTime: number;
    private performanceMetrics: Map<string, number> = new Map();
    private userCache: Map<number, string> = new Map(); // 缓存用户名，避免重复获取

    constructor() {
        this.log = logger('AiLogger');
        this.sessionId = this.generateSessionId();
        this.sessionStartTime = Date.now();
        this.initialize();
    }

    /**
     * 初始化AI日志系统
     */
    private async initialize() {
        try {
            this.storage = new IDBStorage(DATABASE_STATE, 'aiLogs');
            this.initialized = true;
            this.log('AI日志系统初始化成功');
        } catch (error) {
            this.log.error && this.log.error('AI日志系统初始化失败:', error) ||
                this.log('AI日志系统初始化失败:', error);
            this.initialized = false;
        }
    }

    /**
     * 重新初始化日志系统（当遇到问题时可调用）
     */
    public async reinitialize() {
        if (!this.initialized) {
            await this.initialize();
            return;
        }
    }

    /**
     * 生成会话ID
     * @returns 会话ID
     */
    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }

    /**
     * 格式化时间戳
     * @param timestamp 时间戳
     * @returns 格式化的时间字符串
     */
    private formatTimestamp(timestamp: number): string {
        const date = new Date(timestamp);
        return date.toISOString();
    }

    /**
     * 获取用户fullName作为缓存
     * @param peerId 用户ID
     * @returns 用户全名
     */
    private async getUserFullName(peerId: number): Promise<string> {
        // 从缓存中获取用户名，如果没有则调用API获取
        if (this.userCache.has(peerId)) {
            return this.userCache.get(peerId);
        }

        try {
            const fullName = await getFullNameByPeerId(peerId);
            if (fullName) {
                this.userCache.set(peerId, fullName);
                return fullName;
            }
        } catch (error) {
            this.log('获取用户全名失败:', error);
        }

        // 如果获取失败，使用默认名称
        return `未知用户_${peerId}`;
    }

    /**
     * 生成存储键（peerId + fullName）
     * @param peerId 用户ID
     * @returns 存储键
     */
    private async getUserStorageKey(peerId: number): Promise<string> {
        const fullName = await this.getUserFullName(peerId);
        return `${peerId}_${fullName}`;
    }

    /**
     * 添加日志项
     * @param peerId 用户ID
     * @param logItem 日志项
     */
    private async addLogItem(peerId: number, logItem: AiLogItem) {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法添加日志项');
            return;
        }

        try {
            // 使用peerId与fullName组合作为key
            const storageKey = await this.getUserStorageKey(peerId);

            // 获取用户的所有会话日志
            const userSessions = await this.storage.get<{ [sessionId: string]: AiLogItem[] }>(storageKey) || {};

            // 确保用户会话对象存在
            if (!userSessions || typeof userSessions !== 'object') {
                this.log(`用户 ${peerId} 的会话日志对象不存在或无效，创建新的`);
            }

            // 会话ID（用于追踪完整的会话流程）
            const sessionId = logItem.sessionId || this.sessionId;

            // 确保当前会话的日志数组存在
            if (!userSessions[sessionId]) {
                userSessions[sessionId] = [];
            }

            // 添加新的日志项到当前会话
            userSessions[sessionId].push({
                ...logItem,
                sessionId
            });

            // 限制每个会话的日志数量，防止无限增长
            const maxLogsPerSession = 200; // 每个会话最多保存200条日志
            if (userSessions[sessionId].length > maxLogsPerSession) {
                userSessions[sessionId].splice(0, userSessions[sessionId].length - maxLogsPerSession);
            }

            // 限制会话总数
            const maxSessions = 10; // 每个用户最多保存10个会话
            const sessionIds = Object.keys(userSessions);
            if (sessionIds.length > maxSessions) {
                // 按时间排序，删除最旧的会话
                const sortedSessions = sessionIds
                    .map(id => ({
                        id,
                        lastTime: userSessions[id].length > 0
                            ? userSessions[id][userSessions[id].length - 1].timestamp
                            : 0
                    }))
                    .sort((a, b) => a.lastTime - b.lastTime);

                // 删除最旧的会话直到达到上限
                for (let i = 0; i < sortedSessions.length - maxSessions; i++) {
                    delete userSessions[sortedSessions[i].id];
                }
            }

            // 保存更新后的会话日志
            await this.storage.save(storageKey, userSessions);

            // 控制台输出，便于调试
            console.log(`[AI日志] ${logItem.formattedTime} [${logItem.level}] ${logItem.type}: ${logItem.message} (会话: ${sessionId})`);
        } catch (error) {
            this.log('添加AI日志项失败:', error);
            // 尝试恢复
            this.initialized = false;
            this.reinitialize();
        }
    }

    /**
     * 1. 开始AI会话
     * @param peerId 用户ID
     * @param options 会话选项
     */
    public startSession(peerId: number, options: any): void {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法开始会话');
            return;
        }

        this.sessionId = this.generateSessionId();
        this.sessionStartTime = Date.now();

        const timestamp = Date.now();
        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.AI_SESSION_START,
            level: AiLogLevel.INFO,
            message: `用户 ${peerId} 的AI会话开始`,
            sessionId: this.sessionId,
            data: options
        });
    }

    /**
     * 2. 记录收到的用户消息
     * @param peerId 用户ID
     * @param message 消息内容
     * @param messageData 消息数据
     */
    public logReceivedMessage(peerId: number, message: string, messageData: any): void {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法记录用户消息');
            return;
        }

        const timestamp = Date.now();
        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.RECEIVED_MESSAGE,
            level: AiLogLevel.INFO,
            message: `收到用户 ${peerId} 的消息: ${message}`,
            sessionId: this.sessionId,
            data: messageData
        });
    }

    /**
     * 3. 记录AI发送的消息
     * @param peerId 用户ID
     * @param message 消息内容
     * @param messageType 消息类型
     * @param data 附加数据
     */
    public logAiMessageSent(peerId: number, message: string, messageType: string, data: any): void {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法记录AI消息');
            return;
        }

        const timestamp = Date.now();
        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.AI_MESSAGE_SENT,
            level: AiLogLevel.INFO,
            message: `向用户 ${peerId} 发送AI消息: ${message}`,
            sessionId: this.sessionId,
            data: { messageType, data }
        });
    }

    /**
     * 4. 记录AI查询响应
     * @param peerId 用户ID
     * @param responseData 响应数据
     */
    public logAiQueryResponse(peerId: number, responseData: any): void {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法记录AI查询响应');
            return;
        }

        const timestamp = Date.now();
        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.AI_QUERY_RESPONSE,
            level: AiLogLevel.INFO,
            message: `查询用户 ${peerId} 的AI响应`,
            sessionId: this.sessionId,
            data: responseData
        });
    }

    /**
     * 4.1 记录query_chat接口响应
     * @param peerId 用户ID
     * @param queryResponse query_chat接口响应
     * @param attemptNumber 第几次查询（可选）
     */
    public logQueryChat(peerId: number, queryResponse: any, attemptNumber?: number): void {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法记录query_chat响应');
            return;
        }

        const timestamp = Date.now();
        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.QUERY_CHAT,
            level: AiLogLevel.INFO,
            message: `第${attemptNumber || '?'}次查询用户 ${peerId} 的query_chat响应`,
            sessionId: this.sessionId,
            data: {
                attemptNumber: attemptNumber || 0,
                response: queryResponse
            }
        });
    }

    /**
     * 5. 结束AI会话
     * @param peerId 用户ID
     * @param endReason 结束原因，来自query_chat接口的响应体
     */
    public endSession(peerId: number, endReason?: any): void {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法结束会话');
            return;
        }

        const timestamp = Date.now();
        const sessionDuration = timestamp - this.sessionStartTime;

        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.AI_SESSION_END,
            level: AiLogLevel.INFO,
            message: `用户 ${peerId} 的AI会话结束，持续时间: ${sessionDuration}ms`,
            sessionId: this.sessionId,
            data: {
                sessionDuration,
                endReason: endReason || 'No reason provided'
            }
        });

        // 会话结束后生成新的会话ID
        this.sessionId = this.generateSessionId();
    }

    /**
     * 6. 记录错误日志
     * @param peerId 用户ID
     * @param error 错误信息
     * @param context 错误上下文
     */
    public logError(peerId: number, error: any, context: string): void {
        if (!this.initialized) {
            console.error(`AI日志系统未初始化，无法记录错误: ${error}`);
            return;
        }

        const timestamp = Date.now();
        const errorMessage = error instanceof Error ? error.message : String(error);
        const stackTrace = error instanceof Error ? error.stack : undefined;

        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.ERROR,
            level: AiLogLevel.ERROR,
            message: `用户 ${peerId} 处理时发生错误: ${errorMessage}`,
            sessionId: this.sessionId,
            data: {
                context,
                error: errorMessage,
                stack: stackTrace
            }
        });
    }

    /**
     * 7. 开始性能监控
     * @param metricName 指标名称
     */
    public startPerformanceMetric(metricName: string): void {
        this.performanceMetrics.set(metricName, performance.now());
    }

    /**
     * 8. 结束性能监控并记录
     * @param peerId 用户ID
     * @param metricName 指标名称
     * @param context 上下文信息
     */
    public endPerformanceMetric(peerId: number, metricName: string, context: string = ''): void {
        if (!this.initialized) {
            return;
        }

        const startTime = this.performanceMetrics.get(metricName);
        if (!startTime) {
            return;
        }

        const endTime = performance.now();
        const duration = endTime - startTime;
        this.performanceMetrics.delete(metricName);

        const timestamp = Date.now();
        this.addLogItem(peerId, {
            timestamp,
            formattedTime: this.formatTimestamp(timestamp),
            type: AiLogType.PERFORMANCE,
            level: AiLogLevel.DEBUG,
            message: `用户 ${peerId} 的性能指标: ${metricName} - ${duration.toFixed(2)}ms`,
            sessionId: this.sessionId,
            data: {
                metricName,
                duration,
                context
            }
        });
    }

    /**
     * 9. 获取用户的所有日志
     * @param peerId 用户ID
     * @returns 用户的所有日志（扁平化）
     */
    public async getUserLogs(peerId: number): Promise<AiLogItem[]> {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法获取用户日志');
            return [];
        }

        try {
            const key = await this.getUserStorageKey(peerId);
            const userSessions = await this.storage.get<{ [sessionId: string]: AiLogItem[] }>(key) || {};

            // 扁平化所有会话的日志
            const allLogs: AiLogItem[] = [];
            for (const sessionId in userSessions) {
                if (userSessions[sessionId] && Array.isArray(userSessions[sessionId])) {
                    allLogs.push(...userSessions[sessionId]);
                }
            }

            // 按时间排序
            return allLogs.sort((a, b) => a.timestamp - b.timestamp);
        } catch (error) {
            this.log('获取用户日志失败:', error);
            return [];
        }
    }

    /**
     * 10. 获取特定会话的日志
     * @param peerId 用户ID
     * @param sessionId 会话ID
     * @returns 指定会话的日志
     */
    public async getSessionLogs(peerId: number, sessionId: string): Promise<AiLogItem[]> {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法获取会话日志');
            return [];
        }

        try {
            const key = await this.getUserStorageKey(peerId);
            const userSessions = await this.storage.get<{ [sessionId: string]: AiLogItem[] }>(key) || {};

            if (userSessions[sessionId] && Array.isArray(userSessions[sessionId])) {
                return userSessions[sessionId];
            }

            return [];
        } catch (error) {
            this.log('获取会话日志失败:', error);
            return [];
        }
    }

    /**
     * 11. 获取用户的所有会话ID
     * @param peerId 用户ID
     * @returns 会话ID数组
     */
    public async getUserSessionIds(peerId: number): Promise<string[]> {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法获取用户会话ID');
            return [];
        }

        try {
            const key = await this.getUserStorageKey(peerId);
            const userSessions = await this.storage.get<{ [sessionId: string]: AiLogItem[] }>(key) || {};
            return Object.keys(userSessions);
        } catch (error) {
            this.log('获取用户会话ID失败:', error);
            return [];
        }
    }

    /**
     * 12. 获取会话摘要信息
     * @param peerId 用户ID
     * @returns 会话摘要信息数组
     */
    public async getSessionSummaries(peerId: number): Promise<Array<{
        sessionId: string;
        startTime: number;
        endTime: number;
        messageCount: number;
        firstMessage?: string;
    }>> {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法获取会话摘要');
            return [];
        }

        try {
            const key = await this.getUserStorageKey(peerId);
            const userSessions = await this.storage.get<{ [sessionId: string]: AiLogItem[] }>(key) || {};

            return Object.keys(userSessions).map(sessionId => {
                const logs = userSessions[sessionId];
                if (!logs || !Array.isArray(logs) || logs.length === 0) {
                    return {
                        sessionId,
                        startTime: 0,
                        endTime: 0,
                        messageCount: 0
                    };
                }

                // 按时间排序
                logs.sort((a, b) => a.timestamp - b.timestamp);

                // 获取第一条用户消息（如果有）
                const firstUserMessage = logs.find(log => log.type === AiLogType.RECEIVED_MESSAGE);

                return {
                    sessionId,
                    startTime: logs[0].timestamp,
                    endTime: logs[logs.length - 1].timestamp,
                    messageCount: logs.length,
                    firstMessage: firstUserMessage ? firstUserMessage.message : undefined
                };
            }).sort((a, b) => b.endTime - a.endTime); // 按最后更新时间倒序排列
        } catch (error) {
            this.log('获取会话摘要失败:', error);
            return [];
        }
    }

    /**
     * 13. 清理旧日志
     * @param days 保留多少天内的日志
     */
    public async cleanOldLogs(days: number = 7): Promise<void> {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法清理旧日志');
            return;
        }

        try {
            const cutoffTime = Date.now() - (days * 24 * 60 * 60 * 1000);
            const keys = await this.storage.getKeys();

            for (const key of keys) {
                try {
                    const userSessions = await this.storage.get<{ [sessionId: string]: AiLogItem[] }>(key) || {};
                    let hasChanges = false;

                    // 遍历每个会话，清理过期的会话
                    for (const sessionId in userSessions) {
                        if (!userSessions[sessionId] || !Array.isArray(userSessions[sessionId])) {
                            continue;
                        }

                        // 检查会话的最后活动时间
                        const sessionLogs = userSessions[sessionId];
                        if (sessionLogs.length === 0) {
                            delete userSessions[sessionId];
                            hasChanges = true;
                            continue;
                        }

                        // 获取会话的最后活动时间
                        const lastActivityTime = Math.max(...sessionLogs.map(log => log.timestamp));

                        // 如果会话过期，删除整个会话
                        if (lastActivityTime < cutoffTime) {
                            delete userSessions[sessionId];
                            hasChanges = true;
                        }
                    }

                    // 如果有变更，保存更新后的会话数据
                    if (hasChanges) {
                        await this.storage.save(key, userSessions);
                    }
                } catch (error) {
                    this.log(`处理键 ${key} 的日志清理失败:`, error);
                }
            }

            this.log(`已清理 ${days} 天前的AI日志`);
        } catch (error) {
            this.log('清理旧日志失败:', error);
        }
    }

    /**
     * 14. 添加日志项（直接使用键名）
     * @param key 存储键
     * @param logItem 日志项
     */
    private async addLogItemWithKey(key: string, logItem: AiLogItem) {
        if (!this.initialized) {
            this.log('AI日志系统未初始化，无法添加日志项');
            return;
        }

        try {
            // 获取用户的所有会话日志
            const userSessions = await this.storage.get<{ [sessionId: string]: AiLogItem[] }>(key) || {};

            // 会话ID
            const sessionId = logItem.sessionId || this.sessionId;

            // 确保当前会话的日志数组存在
            if (!userSessions[sessionId]) {
                userSessions[sessionId] = [];
            }

            // 添加新的日志项到当前会话
            userSessions[sessionId].push(logItem);

            // 限制每个会话的日志数量
            const maxLogsPerSession = 200;
            if (userSessions[sessionId].length > maxLogsPerSession) {
                userSessions[sessionId].splice(0, userSessions[sessionId].length - maxLogsPerSession);
            }

            // 保存更新后的会话日志
            await this.storage.save(key, userSessions);

            // 控制台输出，便于调试
            console.log(`[AI日志] ${logItem.formattedTime} [${logItem.level}] ${logItem.type}: ${logItem.message} (会话: ${sessionId})`);
        } catch (error) {
            this.log('添加AI日志项失败:', error);
            this.initialized = false;
            this.reinitialize();
        }
    }
}

// 单例模式导出
const aiLogger = new AiLogger();
export default aiLogger; 